import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { differenceInCalendarDays, startOfDay } from 'date-fns';
import { Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import { PolicyListItem } from '../../services/api/policyService';
import { formatTimestamp } from '../../utils/utils';

const MAX_VISIBLE_TAGS = 3;

const getEffectiveBadge = (effectiveDate?: string) => {
    if (!effectiveDate) return null;

    const effective = startOfDay(new Date(effectiveDate));
    const today = startOfDay(new Date());
    const daysUntil = differenceInCalendarDays(effective, today);

    if (daysUntil <= 0) return null;

    return {
        days: daysUntil,
        className:
            'h-5 whitespace-nowrap rounded-md border-blue-500/20 bg-blue-500/10 px-1.5 py-0 text-[11px] font-medium text-blue-300 hover:bg-blue-500/10',
    };
};

const getExpiryWarning = (expiryDate?: string) => {
    if (!expiryDate) return null;

    const expiry = startOfDay(new Date(expiryDate));
    const today = startOfDay(new Date());
    const daysUntilExpiry = differenceInCalendarDays(expiry, today);

    if (daysUntilExpiry < 0 || daysUntilExpiry > 30) {
        return null;
    }

    if (daysUntilExpiry <= 14) {
        return {
            days: daysUntilExpiry,
            className:
                'h-5 whitespace-nowrap rounded-md border-red-500/20 bg-red-500/10 px-1.5 py-0 text-[11px] font-medium text-red-300 hover:bg-red-500/10',
        };
    }

    return {
        days: daysUntilExpiry,
        className:
            'h-5 whitespace-nowrap rounded-md border-amber-500/20 bg-amber-500/10 px-1.5 py-0 text-[11px] font-medium text-amber-200 hover:bg-amber-500/10',
    };
};

export interface PolicyTableProps {
    policies: PolicyListItem[];
    onDelete?: (policy: PolicyListItem) => void;
    highlightExpiredDates?: boolean;
    showEffectiveDate?: boolean;
}

export function PolicyTable({
    policies,
    onDelete,
    highlightExpiredDates = false,
    showEffectiveDate = false,
}: PolicyTableProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    return (
        <TooltipProvider delayDuration={300}>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('departmentPolicies.table.title')}</TableHead>
                            <TableHead className="w-[220px]">{t('departmentPolicies.table.tags')}</TableHead>
                            <TableHead>{t('departmentPolicies.table.author')}</TableHead>
                            <TableHead>{t('departmentPolicies.table.updated')}</TableHead>
                            {showEffectiveDate && <TableHead>{t('departmentPolicies.table.effective')}</TableHead>}
                            <TableHead>{t('departmentPolicies.table.expires')}</TableHead>
                            {onDelete && <TableHead className="w-auto" />}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {policies.map((policy) => {
                            const tags = policy.tags ?? [];
                            const visibleTags = tags.slice(0, MAX_VISIBLE_TAGS);
                            const overflowCount = tags.length - MAX_VISIBLE_TAGS;
                            const effectiveBadge = showEffectiveDate
                                ? getEffectiveBadge(policy.effectiveDate)
                                : null;
                            const expiryWarning = highlightExpiredDates
                                ? null
                                : getExpiryWarning(policy.expiryDate);

                            return (
                                <TableRow
                                    key={policy._id}
                                    className="cursor-pointer"
                                    onClick={() => navigate(`/department-policies/${policy._id}`)}
                                >
                                    <TableCell className="font-medium">{policy.title}</TableCell>
                                    <TableCell className="w-[220px] max-w-[220px]">
                                        {tags.length > 0 ? (
                                            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                                                {visibleTags.map((tag) => (
                                                    <Badge
                                                        key={tag}
                                                        variant="secondary"
                                                        className="whitespace-nowrap font-normal text-xs bg-zinc-200 text-zinc-800 border-0 hover:bg-zinc-200"
                                                    >
                                                        {tag}
                                                    </Badge>
                                                ))}
                                                {overflowCount > 0 && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <span className="inline-flex shrink-0">
                                                                <Badge
                                                                    variant="secondary"
                                                                    className="shrink-0 whitespace-nowrap font-normal text-xs bg-muted/50 text-muted-foreground/70 border-0 hover:bg-muted/50"
                                                                >
                                                                    +{overflowCount}
                                                                </Badge>
                                                            </span>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            {tags.slice(MAX_VISIBLE_TAGS).join(', ')}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-muted-foreground/50">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {policy.createdBy}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {formatTimestamp(policy.updatedAt)}
                                    </TableCell>
                                    {showEffectiveDate && (
                                        <TableCell>
                                            {policy.effectiveDate ? (
                                                <div className="flex items-center gap-2 whitespace-nowrap">
                                                    <span className="text-muted-foreground">
                                                        {formatTimestamp(policy.effectiveDate)}
                                                    </span>
                                                    {effectiveBadge && (
                                                        <Badge
                                                            variant="outline"
                                                            className={effectiveBadge.className}
                                                        >
                                                            {t('departmentPolicies.table.effectiveInDays', { days: effectiveBadge.days })}
                                                        </Badge>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        {policy.expiryDate ? (
                                            <div className="flex items-center gap-2 whitespace-nowrap">
                                                <span
                                                    className={
                                                        highlightExpiredDates
                                                            ? 'text-red-400'
                                                            : 'text-muted-foreground'
                                                    }
                                                >
                                                    {formatTimestamp(policy.expiryDate)}
                                                </span>
                                                {expiryWarning && (
                                                    <Badge
                                                        variant="outline"
                                                        className={expiryWarning.className}
                                                    >
                                                        {t('departmentPolicies.table.daysLeft', { days: expiryWarning.days })}
                                                    </Badge>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-muted-foreground">—</span>
                                        )}
                                    </TableCell>
                                    {onDelete && (
                                        <TableCell>
                                            <div className="flex items-center gap-1.5">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="gap-1.5 border-subtle text-muted-foreground hover:text-foreground"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/department-policies/${policy._id}/edit`);
                                                    }}
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                    {t('departmentPolicies.table.edit')}
                                                </Button>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-muted-foreground/50 hover:text-muted-foreground"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onDelete(policy);
                                                            }}
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{t('departmentPolicies.table.delete')}</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
        </TooltipProvider>
    );
}
